// Admin CRM list — same column structure as etoh.io: first/last name, company,
// email, category, country, language, status, tags. Click → /admin/crm/:id.
import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, Upload, Linkedin, Instagram, Twitter, Globe, Facebook, Music2 } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import CrmDashboard from "@/components/admin/CrmDashboard";

const STATUSES = ["lead", "contacted", "qualified", "customer", "lost"];
const STATUS_LABEL = {
  lead: "Nouveau",
  contacted: "Contacté",
  qualified: "Qualifié",
  customer: "Client",
  lost: "Perdu",
};
const STATUS_COLOR = {
  lead: "bg-[color:var(--gv-pink)]/15 text-[color:var(--gv-pink)]",
  contacted: "bg-[color:var(--gv-blue)]/15 text-[color:var(--gv-blue)]",
  qualified: "bg-emerald-100 text-emerald-700",
  customer: "bg-amber-100 text-amber-700",
  lost: "bg-rose-100 text-rose-700",
};

// Same labels as etoh.io
export const CATEGORY_LABELS = {
  domaines_viticoles: "Domaines viticoles",
  cavistes: "Cavistes",
  negociants: "Négociants",
  restaurateurs: "Restaurateurs",
  distributeurs: "Distributeurs",
  importateurs: "Importateurs",
  e_commerce: "E-commerce",
  ecoles_du_vin: "Écoles du vin",
  drinks_tech: "Drinks Tech (startups)",
  autre: "Autre",
};

const AdminCRMPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(params.get("import") === "1");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleId = (id) => setSelectedIds((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelectedIds((s) => {
    if (s.size >= items.length) return new Set();
    return new Set(items.map((c) => c.id));
  });
  const clearSelection = () => setSelectedIds(new Set());

  const bulkDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Supprimer définitivement ${selectedIds.size} contact(s) ?`)) return;
    setBulkBusy(true);
    try {
      const r = await adminApi.bulkDeleteContacts([...selectedIds]);
      alert(`✓ ${r.deleted} contact(s) supprimé(s)`);
      clearSelection(); await load();
    } catch (e) { alert(e.message); }
    finally { setBulkBusy(false); }
  };

  const bulkStartSequence = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Démarrer la séquence prospection B2B sur ${selectedIds.size} contact(s) ?\nLa langue + catégorie de chaque contact détermine quel copy est envoyé.`)) return;
    setBulkBusy(true);
    try {
      const r = await adminApi.bulkStartSequence([...selectedIds]);
      const reasons = Object.entries(r.skipped_reasons || {}).map(([k, v]) => `${k}: ${v}`).join(", ");
      const errs = Array.isArray(r.send_errors) ? r.send_errors : [];
      const errSummary = errs.length
        ? `\n\n⚠️ ${errs.length} email(s) en erreur :\n` +
          errs.slice(0, 5).map((e) => `  • ${e.email || e.contact_id} (step ${e.step}) — ${e.error}`).join("\n") +
          (errs.length > 5 ? `\n  …et ${errs.length - 5} autre(s)` : "")
        : "";
      alert(
        `✓ ${r.started} séquence(s) démarrée(s) sur ${r.selected} sélectionné(s)` +
        (r.sent_now > 0 ? `\n📤 ${r.sent_now} email(s) J0 envoyé(s) immédiatement` : "") +
        (r.skipped > 0 ? `\n${r.skipped} skippé(s) — ${reasons}` : "") +
        (r.tick_error ? `\n\n❌ Erreur générale : ${r.tick_error}` : "") +
        errSummary
      );
      clearSelection(); await load();
    } catch (e) { alert(e.message); }
    finally { setBulkBusy(false); }
  };

  const allSelected = items.length > 0 && selectedIds.size >= items.length;

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listContacts({ q, status, limit: 200 });
      let rows = data.items || [];
      if (category) rows = rows.filter((r) => r.category === category);
      setItems(rows);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, category]);
  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [q]);

  const syncParams = (next) => {
    const sp = new URLSearchParams();
    if (next.q ?? q) sp.set("q", next.q ?? q);
    if (next.status ?? status) sp.set("status", next.status ?? status);
    if (next.category ?? category) sp.set("category", next.category ?? category);
    setParams(sp);
  };

  return (
    <div data-testid="admin-crm">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] mb-1">CRM</h1>
          <p className="text-sm text-[color:var(--gv-ink)]/60">{total} contact{total > 1 ? "s" : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowImport(true)} className="btn-secondary" data-testid="crm-import-btn">
            <Upload size={14} /> Import CSV
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="crm-create-btn">
            <Plus size={14} /> Nouveau contact
          </button>
        </div>
      </div>

      {/* Visual dashboard — pipeline vs goals */}
      <CrmDashboard />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5 bg-white border border-[color:var(--gv-line)] rounded-[5px] p-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); syncParams({ q: e.target.value }); }}
            placeholder="Rechercher nom, email, société, pays…"
            className="w-full bg-transparent pl-10 pr-3 py-2 text-sm focus:outline-none"
            data-testid="crm-search"
          />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); syncParams({ status: e.target.value }); }} className="text-sm border border-[color:var(--gv-line)] rounded-[4px] px-2 py-2 bg-white" data-testid="crm-status-filter">
          <option value="">Tous statuts</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <select value={category} onChange={(e) => { setCategory(e.target.value); syncParams({ category: e.target.value }); }} className="text-sm border border-[color:var(--gv-line)] rounded-[4px] px-2 py-2 bg-white" data-testid="crm-category-filter">
          <option value="">Toutes catégories</option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden">
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-[color:var(--gv-pink)]/8 border-b border-[color:var(--gv-line)]" data-testid="bulk-toolbar">
            <span className="text-[13px] font-medium">{selectedIds.size} sélectionné{selectedIds.size > 1 ? "s" : ""}</span>
            <button onClick={bulkStartSequence} disabled={bulkBusy} className="btn-primary text-xs disabled:opacity-50" data-testid="bulk-start-sequence">
              {bulkBusy ? "…" : "▶"} Démarrer séquence B2B
            </button>
            <button onClick={bulkDelete} disabled={bulkBusy} className="btn-secondary text-xs text-rose-600 border-rose-300 hover:bg-rose-50 disabled:opacity-50" data-testid="bulk-delete">
              {bulkBusy ? "…" : "🗑"} Supprimer
            </button>
            <button onClick={clearSelection} className="text-xs text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-ink)] ml-auto" data-testid="bulk-clear">
              Annuler
            </button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="crm-table">
            <thead className="bg-[color:var(--gv-mist)] text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-[color:var(--gv-pink)]" data-testid="bulk-select-all" />
                </th>
                <Th>Nom</Th><Th>Société</Th><Th>Email</Th><Th>Catégorie</Th><Th>Pays</Th><Th>Langue</Th><Th>Statut</Th><Th>Réseaux</Th><Th>MAJ</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={10} className="py-10 text-center text-[color:var(--gv-ink)]/50">Chargement…</td></tr>)}
              {!loading && items.length === 0 && (<tr><td colSpan={10} className="py-10 text-center text-[color:var(--gv-ink)]/50">Aucun contact. Importe un CSV ou crée-en un.</td></tr>)}
              {!loading && items.map((c) => {
                const displayName = (c.first_name || c.last_name) ? `${c.first_name || ""} ${c.last_name || ""}`.trim() : (c.name || "—");
                const isSel = selectedIds.has(c.id);
                return (
                  <tr key={c.id} className={`border-t border-[color:var(--gv-line)] cursor-pointer ${isSel ? "bg-[color:var(--gv-pink)]/5" : "hover:bg-[color:var(--gv-mist)]/60"}`} data-testid={`crm-row-${c.id}`}>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={isSel} onChange={() => toggleId(c.id)} className="accent-[color:var(--gv-pink)] cursor-pointer w-4 h-4" data-testid={`bulk-select-${c.id}`} />
                    </td>
                    <Td onClick={() => navigate(`/admin/crm/${c.id}`)}><span className="font-medium text-[color:var(--gv-ink)]">{displayName}</span></Td>
                    <Td onClick={() => navigate(`/admin/crm/${c.id}`)}>{c.company || "—"}</Td>
                    <Td onClick={() => navigate(`/admin/crm/${c.id}`)}>{c.email || "—"}</Td>
                    <Td onClick={() => navigate(`/admin/crm/${c.id}`)}>{c.category ? (CATEGORY_LABELS[c.category] || c.category) : "—"}</Td>
                    <Td onClick={() => navigate(`/admin/crm/${c.id}`)}>{c.country || "—"}</Td>
                    <Td onClick={() => navigate(`/admin/crm/${c.id}`)} className="uppercase text-[11.5px] text-[color:var(--gv-ink)]/60">{c.language || "—"}</Td>
                    <Td onClick={() => navigate(`/admin/crm/${c.id}`)}>
                      <span className={`px-2 py-0.5 rounded-[3px] text-[11px] uppercase tracking-widest ${STATUS_COLOR[c.status] || "bg-gray-100 text-gray-600"}`}>
                        {STATUS_LABEL[c.status] || c.status || "lead"}
                      </span>
                    </Td>
                    <Td onClick={() => navigate(`/admin/crm/${c.id}`)}><SocialIcons socials={c.socials || {}} /></Td>
                    <Td onClick={() => navigate(`/admin/crm/${c.id}`)} className="text-[11.5px] text-[color:var(--gv-ink)]/50">{formatDate(c.updated_at)}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && <CreateContactModal onClose={() => setShowCreate(false)} onCreated={(c) => { setShowCreate(false); navigate(`/admin/crm/${c.id}`); }} />}
      {showImport && <ImportCsvModal onClose={() => { setShowImport(false); setParams({}); load(); }} />}
    </div>
  );
};

const Th = ({ children }) => <th className="text-left px-4 py-3 font-medium">{children}</th>;
const Td = ({ children, className = "", onClick }) => <td onClick={onClick} className={`px-4 py-3 ${className}`}>{children}</td>;

const SocialIcons = ({ socials }) => {
  const items = [
    { k: "linkedin", Icon: Linkedin },
    { k: "instagram", Icon: Instagram },
    { k: "twitter", Icon: Twitter },
    { k: "facebook", Icon: Facebook },
    { k: "tiktok", Icon: Music2 },
    { k: "website", Icon: Globe },
  ].filter((i) => socials[i.k]);
  if (!items.length) return <span className="text-[color:var(--gv-ink)]/30">—</span>;
  return (
    <span className="inline-flex items-center gap-2">
      {items.map(({ k, Icon }) => (
        <a key={k} href={socials[k]} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-pink)]" title={k}>
          <Icon size={14} />
        </a>
      ))}
    </span>
  );
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
}

const Modal = ({ children, onClose, title, testId, size = "lg" }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid={testId}>
    <div className={`bg-white rounded-[5px] ${size === "xl" ? "max-w-4xl" : "max-w-2xl"} w-full max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
      <div className="px-6 py-4 border-b border-[color:var(--gv-line)] flex items-center justify-between sticky top-0 bg-white z-10">
        <h3 className="font-display text-xl text-[color:var(--gv-ink)]">{title}</h3>
        <button onClick={onClose} className="text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-ink)]" aria-label="Close">✕</button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

// EtOH-style 14-field contact form (3-col responsive grid)
const CreateContactModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    company: "", position: "", language: "",
    socials: { linkedin: "", instagram: "", facebook: "", twitter: "", tiktok: "", website: "" },
    country: "", category: "", tags: "", status: "lead", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updSocial = (k, v) => setForm((f) => ({ ...f, socials: { ...f.socials, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { setErr("Prénom requis"); return; }
    setLoading(true); setErr("");
    try {
      const payload = {
        ...form,
        email: form.email || null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      const c = await adminApi.createContact(payload);
      onCreated(c);
    } catch (e) {
      setErr(e.message || String(e));
    } finally { setLoading(false); }
  };

  return (
    <Modal title="Nouveau contact" onClose={onClose} testId="modal-create-contact" size="xl">
      <form onSubmit={submit} className="space-y-5">
        <Row3>
          <Field label="Prénom *" v={form.first_name} onChange={(v) => upd("first_name", v)} testId="create-field-first_name" required />
          <Field label="Nom" v={form.last_name} onChange={(v) => upd("last_name", v)} testId="create-field-last_name" />
          <Field label="Email" type="email" v={form.email} onChange={(v) => upd("email", v)} testId="create-field-email" />
        </Row3>
        <Row3>
          <Field label="Téléphone" v={form.phone} onChange={(v) => upd("phone", v)} testId="create-field-phone" />
          <Field label="Société" v={form.company} onChange={(v) => upd("company", v)} testId="create-field-company" />
          <Field label="Poste" v={form.position} onChange={(v) => upd("position", v)} testId="create-field-position" />
        </Row3>
        <Row3>
          <SelectField label="Langue" v={form.language} onChange={(v) => upd("language", v)} testId="create-field-language" options={[["", "—"], ["EN", "EN"], ["FR", "FR"], ["ES", "ES"], ["PT", "PT"]]} />
          <Field label="Pays" v={form.country} onChange={(v) => upd("country", v)} testId="create-field-country" />
          <SelectField label="Catégorie" v={form.category} onChange={(v) => upd("category", v)} testId="create-field-category" options={[["", "— Sélectionner —"], ...Object.entries(CATEGORY_LABELS)]} />
        </Row3>
        <Row2>
          <Field label="LinkedIn URL" v={form.socials.linkedin} onChange={(v) => updSocial("linkedin", v)} testId="create-field-linkedin" />
          <Field label="Instagram URL" v={form.socials.instagram} onChange={(v) => updSocial("instagram", v)} testId="create-field-instagram" />
        </Row2>
        <Row2>
          <Field label="Facebook URL" v={form.socials.facebook} onChange={(v) => updSocial("facebook", v)} testId="create-field-facebook" />
          <Field label="X / Twitter URL" v={form.socials.twitter} onChange={(v) => updSocial("twitter", v)} testId="create-field-twitter" />
        </Row2>
        <Row2>
          <Field label="TikTok URL" v={form.socials.tiktok} onChange={(v) => updSocial("tiktok", v)} testId="create-field-tiktok" />
          <Field label="Site Web" v={form.socials.website} onChange={(v) => updSocial("website", v)} testId="create-field-website" />
        </Row2>
        <Row2>
          <Field label="Tags (CSV)" v={form.tags} onChange={(v) => upd("tags", v)} testId="create-field-tags" placeholder="ex: salon-2026, hot-lead" />
          <SelectField label="Statut" v={form.status} onChange={(v) => upd("status", v)} testId="create-field-status" options={STATUSES.map((s) => [s, STATUS_LABEL[s]])} />
        </Row2>
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-1">Notes</div>
          <textarea value={form.notes} onChange={(e) => upd("notes", e.target.value)} rows={3} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm" data-testid="create-field-notes" />
        </label>
        {err && <div className="text-rose-600 text-[13px]" data-testid="create-error">{err}</div>}
        <div className="flex items-center justify-end gap-3 pt-2 border-t border-[color:var(--gv-line)]">
          <button type="button" onClick={onClose} className="text-sm text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-ink)]">Annuler</button>
          <button disabled={loading || !form.first_name} className="btn-primary disabled:opacity-50" data-testid="create-submit">
            {loading ? "Enregistrement…" : "✓ Enregistrer"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const Row2 = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
const Row3 = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>;

const Field = ({ label, v, onChange, type = "text", testId, required, placeholder }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-1">{label}</div>
    <input type={type} value={v} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm focus:border-[color:var(--gv-pink)]/40 outline-none" data-testid={testId} />
  </label>
);

const SelectField = ({ label, v, onChange, options, testId }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-1">{label}</div>
    <select value={v} onChange={(e) => onChange(e.target.value)} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm bg-white focus:border-[color:var(--gv-pink)]/40 outline-none" data-testid={testId}>
      {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
    </select>
  </label>
);

const ImportCsvModal = ({ onClose }) => {
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);          // [{file, status, result?, error?}]
  const [running, setRunning] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = (incoming) => {
    const accepted = Array.from(incoming || []).filter((f) =>
      /\.csv$/i.test(f.name) || f.type === "text/csv" || f.type === "application/vnd.ms-excel"
    );
    setFiles((prev) => [
      ...prev,
      ...accepted.map((file) => ({ file, status: "pending", result: null, error: null })),
    ]);
  };

  const onFileInput = (e) => addFiles(e.target.files);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer?.files);
  };
  const removeFile = (idx) => setFiles((arr) => arr.filter((_, i) => i !== idx));

  const runImport = async () => {
    if (!files.length || running) return;
    setRunning(true);
    // Sequential upload so we don't blast Mongo + can show progress
    for (let i = 0; i < files.length; i += 1) {
      setFiles((arr) => arr.map((f, j) => (j === i ? { ...f, status: "uploading" } : f)));
      try {
        const r = await adminApi.importContactsCsv(files[i].file);
        setFiles((arr) => arr.map((f, j) => (j === i ? { ...f, status: "done", result: r } : f)));
      } catch (e) {
        setFiles((arr) => arr.map((f, j) => (j === i ? { ...f, status: "error", error: e?.message || String(e) } : f)));
      }
    }
    setRunning(false);
  };

  const totalInserted = files.reduce((s, f) => s + (f.result?.inserted || 0), 0);
  const totalSkipped = files.reduce((s, f) => s + (f.result?.skipped || 0), 0);
  const allDone = files.length > 0 && files.every((f) => f.status === "done" || f.status === "error");

  return (
    <Modal title="Importer des contacts (CSV)" onClose={onClose} testId="modal-import-csv" size="lg">
      <p className="text-sm text-[color:var(--gv-ink)]/70 mb-4 leading-relaxed">
        Glissez-déposez plusieurs fichiers <code>.csv</code> ou cliquez pour les sélectionner.<br/>
        <strong>Mapping :</strong> <code>name</code> → société (etoh-style) · <code>prenom/nom</code> → personne ·
        <code>email, telephone, poste, langue, pays, categorie, linkedin, instagram, facebook, twitter, tiktok, website, tags, status, notes</code>.<br/>
        Les colonnes en plus (adresse, description, industrie, etc.) sont stockées dans les notes du contact.
        Une ligne sans email ni société est ignorée.
      </p>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-[5px] p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-[color:var(--gv-pink)] bg-[color:var(--gv-pink)]/5" : "border-[color:var(--gv-line)] hover:border-[color:var(--gv-ink)]/30"}`}
        data-testid="import-dropzone"
      >
        <Upload size={24} className="mx-auto mb-2 text-[color:var(--gv-ink)]/40" />
        <div className="text-sm text-[color:var(--gv-ink)]/75 mb-1">
          Glissez vos fichiers .csv ici, ou <span className="text-[color:var(--gv-pink)] underline">parcourir</span>
        </div>
        <div className="text-[11.5px] text-[color:var(--gv-ink)]/50">Plusieurs fichiers acceptés en une fois</div>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          multiple
          onChange={onFileInput}
          className="hidden"
          data-testid="import-file"
        />
      </div>

      {files.length > 0 && (
        <ul className="mt-4 space-y-2 max-h-64 overflow-y-auto" data-testid="import-file-list">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between gap-3 p-3 rounded-[3px] border border-[color:var(--gv-line)] bg-white" data-testid={`import-file-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[color:var(--gv-ink)] truncate">{f.file.name}</div>
                <div className="text-[11.5px] text-[color:var(--gv-ink)]/55">
                  {(f.file.size / 1024).toFixed(1)} KB
                  {f.status === "uploading" && <span className="text-[color:var(--gv-pink)]"> · Import en cours…</span>}
                  {f.status === "done" && <span className="text-emerald-700"> · ✓ {f.result.inserted} insérés, {f.result.skipped} ignorés</span>}
                  {f.status === "error" && <span className="text-rose-600"> · ✗ {f.error}</span>}
                </div>
              </div>
              {f.status === "pending" && !running && (
                <button onClick={() => removeFile(i)} className="text-[color:var(--gv-ink)]/40 hover:text-rose-600 text-sm" data-testid={`import-remove-${i}`}>✕</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {allDone && (
        <div className="mt-4 bg-emerald-50 text-emerald-800 px-4 py-3 rounded-[3px] text-sm" data-testid="import-summary">
          ✅ Terminé · <b>{totalInserted}</b> contacts insérés, <b>{totalSkipped}</b> ignorés sur {files.length} fichier{files.length > 1 ? "s" : ""}.
        </div>
      )}

      <div className="mt-5 flex items-center justify-end gap-3">
        {!allDone && (
          <button onClick={onClose} disabled={running} className="text-sm text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-ink)] disabled:opacity-50">Annuler</button>
        )}
        {!allDone ? (
          <button onClick={runImport} disabled={!files.length || running} className="btn-primary disabled:opacity-50" data-testid="import-submit">
            {running ? "Import en cours…" : `Importer ${files.length || ""} fichier${files.length > 1 ? "s" : ""}`}
          </button>
        ) : (
          <button onClick={onClose} className="btn-primary" data-testid="import-close-done">Fermer</button>
        )}
      </div>
    </Modal>
  );
};

export default AdminCRMPage;
